/** Cartwheel web app for notification `location` (e.g. `apps:/dashboard#user-menu`). */
export type NotificationSurface = 'apps' | 'studio' | 'homepage';

export type ParsedLocation = {
  surface: NotificationSurface;
  pathname: string;
  anchor: string;
  /** Canonical `surface:pathname` + `anchor`. */
  key: string;
};

const SURFACE_ALIASES: Record<string, NotificationSurface> = {
  apps: 'apps',
  applets: 'apps',
  studio: 'studio',
  homepage: 'homepage',
};

function surfaceFromToken(token: string): NotificationSurface | undefined {
  return SURFACE_ALIASES[token.trim().toLowerCase()];
}

function toKey(
  surface: NotificationSurface,
  pathname: string,
  anchor: string,
): string {
  return `${surface}:${pathname}${anchor}`;
}

/** Parses `surface:/path#anchor` placement strings from the API. */
export function parseLocation(location: string): ParsedLocation | undefined {
  const trimmed = location.trim();
  const colonIndex = trimmed.indexOf(':');
  if (colonIndex <= 0) {
    return undefined;
  }

  const surface = surfaceFromToken(trimmed.slice(0, colonIndex));
  if (!surface) {
    return undefined;
  }

  const pathPart = trimmed.slice(colonIndex + 1);
  if (!pathPart.startsWith('/')) {
    return undefined;
  }

  const hashIndex = pathPart.indexOf('#');
  const pathname =
    hashIndex >= 0 ? pathPart.slice(0, hashIndex) : pathPart;
  const anchor = hashIndex >= 0 ? pathPart.slice(hashIndex) : '';

  if (!pathname) {
    return undefined;
  }

  return {
    surface,
    pathname,
    anchor,
    key: toKey(surface, pathname, anchor),
  };
}

/** Legacy applet URL prefixes → canonical root used in notification placements. */
const LEGACY_APP_ROOT: Record<string, string> = {
  'batch-comic': '/comic',
  'batch-mascot': '/mascot',
  'batch-generation': '/generation',
  'batch-swing': '/swing',
};

/** Route pathnames that may match a stored pop-up placement for `pathname`. */
function routePathVariants(pathname: string): string[] {
  const paths = new Set([pathname]);

  if (pathname === '/') {
    paths.add('/dashboard');
  }

  const firstSegment = pathname.split('/').filter(Boolean)[0];
  const legacyRoot = firstSegment && LEGACY_APP_ROOT[firstSegment];
  if (legacyRoot) {
    paths.add(legacyRoot);
  }

  return [...paths];
}

/** Location keys to check in the pop-up queue for the current shell route. */
export function popUpLookupKeys(
  surface: NotificationSurface,
  pathname: string,
  anchor: string,
): string[] {
  return routePathVariants(pathname).map((path) => toKey(surface, path, anchor));
}
